/**
 * Was unterwegs und am Ziel wichtig ist: Tankstellen entlang der Strecke,
 * Parkhäuser dort, wo man ankommt.
 *
 * Die eigenen Kacheln helfen hier nicht - sie enthalten nur, was gerade im
 * Bild ist, und die nächste Tankstelle liegt zwanzig Kilometer voraus. Overpass
 * kann dagegen einen Korridor entlang einer Punktfolge abfragen. Gemessen für
 * eine Strecke von acht Kilometern: 2,8 KB Antwort in 0,9 Sekunden.
 */
/**
 * Zwei Server, der Schweizer zuerst. Gemessen: overpass.osm.ch antwortete in
 * 0,5 Sekunden, overpass-api.de in 2,7 - und letzterer lieferte beim Testen
 * mehrfach einen 504. Auf eine einzelne fremde Instanz kann sich eine
 * Funktion nicht stützen, auf die man sich beim Fahren verlässt.
 *
 * Beide werden von Freiwilligen betrieben. Deshalb genau eine Abfrage je
 * Fahrt, nicht eine je Positionsmeldung.
 */
const OVERPASS = [
  'https://overpass.osm.ch/api/interpreter',
  'https://overpass-api.de/api/interpreter'
];

async function frage(query, signal) {
  let letzterFehler = null;
  for (const url of OVERPASS) {
    try {
      const res = await fetch(url, {
        method: 'POST',
        signal,
        headers: { 'Content-Type': 'text/plain' },
        body: query
      });
      if (!res.ok) throw new Error(`Overpass ${res.status}`);
      const d = await res.json();
      return d.elements || [];
    } catch (e) {
      if (e.name === 'AbortError') throw e;
      letzterFehler = e;
    }
  }
  throw letzterFehler || new Error('Overpass nicht erreichbar');
}

/** Mittelpunkt eines Elements - Flächen liefern `center`. */
function ort(e) {
  if (Number.isFinite(e.lat) && Number.isFinite(e.lon)) return { lat: e.lat, lng: e.lon };
  if (e.center) return { lat: e.center.lat, lng: e.center.lon };
  return null;
}

/**
 * Punktfolge ausdünnen. Overpass nimmt bei `around` beliebig viele Paare, aber
 * jede zusätzliche Stelle kostet Rechenzeit auf einem fremden Server - und 60
 * Stützpunkte decken einen Korridor lückenlos ab, solange der Radius grösser
 * ist als der halbe Punktabstand.
 */
function ausduennen(coordinates, maximal = 60) {
  if (coordinates.length <= maximal) return coordinates;
  const schritt = Math.ceil(coordinates.length / maximal);
  const out = coordinates.filter((_, i) => i % schritt === 0);
  const letzter = coordinates[coordinates.length - 1];
  if (out[out.length - 1] !== letzter) out.push(letzter);
  return out;
}

/** Tankstellen im Korridor um die Route. */
export async function tankstellenEntlang(coordinates, { radius = 300, signal } = {}) {
  if (!coordinates?.length) return [];
  const punkte = ausduennen(coordinates)
    .map(([lng, lat]) => `${lat.toFixed(5)},${lng.toFixed(5)}`)
    .join(',');
  const q = `[out:json][timeout:25];node(around:${radius},${punkte})[amenity=fuel];out body 60;`;
  const els = await frage(q, signal);
  return els
    .map((e) => {
      const p = ort(e);
      if (!p) return null;
      const t = e.tags || {};
      return {
        ...p,
        name: t.name || t.brand || t.operator || 'Tankstelle',
        marke: t.brand || '',
        zeiten: t.opening_hours || '',
        id: `${e.type}/${e.id}`
      };
    })
    .filter(Boolean);
}

/**
 * Parkmöglichkeiten am Ziel. Gefiltert und sortiert, nicht bloss aufgezählt:
 * Parkhaus und Tiefgarage zuerst, dann was einen Namen trägt. Strassenrand und
 * Privatplätze fallen raus - wer navigiert, sucht einen Platz zum Abstellen,
 * keine Liste aller Markierungen im Quartier.
 *
 * `access=customers` bleibt dagegen drin: das Parkhaus unter dem Einkaufs-
 * zentrum ist oft genau das gesuchte. Ein zusätzlicher Ausschluss hatte die
 * Abfrage ausserdem in eine Zeitüberschreitung laufen lassen.
 */
export async function parkenBei(ziel, { radius = 900, signal } = {}) {
  if (!ziel) return [];
  const q = `[out:json][timeout:25];
nwr(around:${radius},${ziel.lat.toFixed(5)},${ziel.lng.toFixed(5)})[amenity=parking][access!=private][parking!=street_side][parking!=lane];
out center tags 60;`;
  const els = await frage(q, signal);

  const RANG = { 'multi-storey': 0, underground: 1, garage_boxes: 2 };
  return els
    .map((e) => {
      const p = ort(e);
      if (!p) return null;
      const t = e.tags || {};
      const art = t.parking || '';
      return {
        ...p,
        name: t.name || (RANG[art] !== undefined ? 'Parkhaus' : 'Parkplatz'),
        art,
        ueberdacht: RANG[art] !== undefined,
        plaetze: Number(t.capacity) || null,
        gebuehr: t.fee === 'yes' ? 'gebührenpflichtig' : t.fee === 'no' ? 'gratis' : '',
        zeiten: t.opening_hours || '',
        id: `${e.type}/${e.id}`
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const ra = RANG[a.art] ?? 9;
      const rb = RANG[b.art] ?? 9;
      if (ra !== rb) return ra - rb;
      const na = a.name.startsWith('Park') ? 1 : 0;
      const nb = b.name.startsWith('Park') ? 1 : 0;
      if (na !== nb) return na - nb;
      return (b.plaetze || 0) - (a.plaetze || 0);
    })
    .slice(0, 8);
}
